























































































































































































import {
  computed,
  defineComponent,
  defineVue3AsyncComponent,
  nextTick,
  ref,
  toRefs,
  useFetch,
  useFetchState
} from '~/utils/nuxt3-migration'
import { faBan } from '@fortawesome/free-solid-svg-icons'
import { format, timeDistanceFromNow } from '~/utils/date'
import { differenceInMinutes } from 'date-fns'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { useNamespacedStore } from '~/compositions/store'
import { useDeps } from '~/compositions/dependency-container'
import AutoTouchService from '~/services/auto-touch/AutoTouchService'
import { useSnackbar } from '~/compositions/snackbar'
import { useI18n } from '~/compositions/i18n'
import {
  AutoTouchOneClick,
  AutoTouchOneClickData
} from '~/models/auto-touch/types'
import StripeService from '~/services/logistics/StripeService'
import CBadge from '~/components/shared/configurable/badge/CBadge.vue'
import { SIMPLE_DATE_FORMAT } from '~/constants/date'
import CPrice from '~/components/shared/configurable/CPrice.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import { ciCredit } from '~/icons/source/solid/credit'
import { ciTouch } from '~/icons/source/solid/touch'
import { LegacyUrlService } from '~/services/legacy/url/LegacyUrlService'
import { ciTimes } from '~/icons/source/regular/times'
import { useRequestError } from '~/compositions/request-error'

export default defineComponent({
  components: {
    CPrice,
    CBadge,
    CConfirmationModal: defineVue3AsyncComponent(() =>
      import('~/components/shared/configurable/modal/CConfirmationModal.vue')
    ),
    CProgressBar: defineVue3AsyncComponent(() =>
      import('~/components/shared/configurable/progress-bar/CProgressBar.vue')
    )
  },
  props: {
    classifiedId: {
      type: Number,
      required: true
    },
    square: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup(props) {
    const { classifiedId } = toRefs(props)
    const { getters } = useNamespacedStore<UserState>(USER_NS)
    const handleRequestError = useRequestError()
    const snackbar = useSnackbar()
    const { t } = useI18n()
    const [autoTouchService, stripeService, legacyUrlService] = useDeps(
      AutoTouchService,
      StripeService,
      LegacyUrlService
    )

    const disabled = ref(false)
    const isOneClickModalVisible = ref(false)
    const isCancelConfirmationModalVisible = ref(false)
    const autoTouchOneClickData = useFetchState(
      'autoTouchOneClickData',
      ref<AutoTouchOneClick | null>(null)
    )
    const loading = useFetchState('loading', ref(false))

    useFetch(fetchData)

    const creditsUrl = computed(() => legacyUrlService.getBuyCreditsUrl())
    const userIsAdmin = computed(() => getters('isAdmin'))
    const paidCredits = computed(() => {
      return getters('paidCredits')
    })
    const isActive = computed<boolean>(() =>
      Boolean(autoTouchOneClickData.value?.autoTouch?.isActive)
    )
    const autoTouchData = computed<AutoTouchOneClickData | null>(() => {
      // shortcut
      return autoTouchOneClickData.value?.autoTouch || null
    })
    const expiresIn = computed(() => {
      if (isActive.value && autoTouchData.value?.expires) {
        return timeDistanceFromNow(autoTouchData.value.expires, {
          addSuffix: false
        }).replace('μήνας', 'μήνα')
      }
      return null
    })
    const expirationDate = computed(() => {
      if (isActive.value && autoTouchData.value?.expires) {
        return format(autoTouchData.value.expires, SIMPLE_DATE_FORMAT)
      }
      return null
    })
    const isPaymentTypeStripe = computed(
      () => autoTouchOneClickData.value?.preferredPayment === 'stripe'
    )
    const paymentVal = computed(() => {
      if (isPaymentTypeStripe.value) {
        return `${autoTouchOneClickData.value?.price} €`
      }
      return autoTouchOneClickData.value?.priceCredits
    })
    const isPaidCreditsEnough = computed(
      () =>
        paidCredits.value >= autoTouchOneClickData.value?.priceCredits ||
        userIsAdmin.value
    )
    const expirePercentage = computed(() => {
      if (isActive.value && autoTouchData.value?.expires) {
        const days = 30
        const totalMinutes = days * 1440 // 1440 is minutes in a day
        const minutesTillEnd = differenceInMinutes(
          new Date(autoTouchData.value.expires),
          Date.now()
        )
        const purePercentage = 100 - (minutesTillEnd / totalMinutes) * 100
        if (purePercentage >= 3) {
          return purePercentage
        }
        // else return a hardcoded value in order to be visually ok
        return 2
      }
      return 100
    })
    const tooltipText = computed(() => {
      if (isPaymentTypeStripe.value) {
        return autoTouchOneClickData.value?.oldPrice
          ? `${t('from')} ${autoTouchOneClickData.value?.oldPrice} €`
          : ''
      }

      return autoTouchOneClickData.value?.oldPriceCredits
        ? `${t('from')} ${autoTouchOneClickData.value?.oldPriceCredits} credits`
        : ''
    })

    async function fetchData() {
      loading.value = true
      try {
        autoTouchOneClickData.value = await autoTouchService.fetchOneClickAutoTouchData(
          classifiedId.value
        )
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }
    function onClick(e: Event) {
      if (process.client && !isActive.value && !loading.value) {
        e.preventDefault()
        e.stopPropagation()
        if (!isPaymentTypeStripe.value) {
          isOneClickModalVisible.value = true
          return
        }
        submitOneClick()
      }
    }
    async function submitOneClick() {
      if (!isPaymentTypeStripe.value && !isPaidCreditsEnough.value) {
        window.location.href = `${window.location.origin}${creditsUrl.value}`
        return
      }
      try {
        loading.value = true
        const res = await autoTouchService.getOneClickDetails({
          classifiedID: classifiedId.value,
          payment: autoTouchOneClickData.value?.preferredPayment
        })

        if (res?.session) {
          stripeService.setSessionCookie(res?.session)
        }
        isOneClickModalVisible.value = false
        loading.value = false
        if (!isPaymentTypeStripe.value) {
          snackbar.success(t('successful_activation'))
          nextTick(
            () => (autoTouchOneClickData.value.autoTouch.isActive = true)
          )
          return
        }
        if (window.location.pathname.match(/iframe/)) {
          window.parent.location.href = res?.url
        } else {
          window.location.href = res?.url
        }
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }
    async function cancelOneClickAutotouch() {
      loading.value = true
      try {
        await autoTouchService.cancelOneClickAutotouch(classifiedId.value)
        if (autoTouchOneClickData.value?.autoTouch?.isActive) {
          autoTouchOneClickData.value.autoTouch.isActive = false
        }
        snackbar.success(t('canceled_oneclick'))
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }

    return {
      paidCredits,
      ciTouch,
      faBan,
      autoTouchData,
      expiresIn,
      expirePercentage,
      disabled,
      autoTouchOneClickData,
      onClick,
      loading,
      expirationDate,
      isActive,
      cancelOneClickAutotouch,
      isCancelConfirmationModalVisible,
      paymentVal,
      ciCredit,
      tooltipText,
      isPaymentTypeStripe,
      isOneClickModalVisible,
      submitOneClick,
      isPaidCreditsEnough,
      ciTimes
    }
  },
  i18n: defineComponentTranslations({
    cancel_autotouch_confrimation_msg: {
      en: 'Are you sure you want to cancel one-click auto renewal?',
      el:
        'Είσαι σίγουρος οτι θέλεις να ακυρώσεις την αυτόματη ανανέωση one-click;'
    },
    cancel_autotouch_credits: {
      en:
        'This action will cancel the auto renewal of this classified (credits are <strong>not refundable</strong>). Do you wish to continue?',
      el:
        'Αυτή η ενέργεια θα ακυρώσει την αυτόματη ανανέωση αυτής της αγγελίας (τα credits <strong>δεν επιστρέφονται</strong>). Eπιθυμείς να συνεχίσεις;'
    },
    one_click: {
      en: 'One-click auto renewal',
      el: 'Αυτόματη ανανέωση one-click'
    },
    buy_one_click: {
      en: 'Do you want to proceed with the one-click auto renewal?',
      el: 'Θέλεις να προχωρήσεις στην αγορά της αυτόματης ανανέωσης one-click;'
    },
    renewals: {
      en: 'renewals',
      el: 'ανανεώσεις'
    },
    cancel_oneclick: {
      en: 'Cancel one-click',
      el: 'Ακύρωση one-click'
    },
    successful_activation: {
      en: 'Successful activation of one click auto renewal',
      el: 'Επιτυχής ενεργοποίηση one-click'
    },
    canceled_oneclick: {
      en: 'One-click auto touch canceled',
      el: 'H αυτόματη ανανέωση one-click ακυρώθηκε'
    },
    not_enough_paid_credits: {
      en: 'Not enough paid credits to activate the one-click auto-renewal.',
      el:
        'Τα πληρωμένα credits σου δεν επαρκούν για να ενεργοποιήσεις την αυτόματη ανανέωσης one-click.'
    }
  })
})
